import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Plus, Loader2, Trash2, Pencil, CheckCircle2, X, History, AlertCircle, AlertTriangle, Clock } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { fmtMoney } from "@/utils/currency";
import { slotRows } from "@/constants/paymentMethods";
import { WorkerPayoutSection } from "@/components/admin/WorkerPayoutSection";

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

const FREQ_LABEL = { weekly: "weekly", biweekly: "every 2 weeks", monthly: "monthly" };
const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

const EMPTY = { staff_user_id: "", role_title: "", amount: "", frequency: "weekly", notes: "", next_due: "" };

// Owner-only staff/management compensation manager. Mounted inside the Payments section.
export const StaffCompensation = () => {
  const [comps, setComps] = useState(null);
  const [summary, setSummary] = useState(null);
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // arrangement being marked paid
  const [payForm, setPayForm] = useState({ amount: "", method: "", transaction_id: "", note: "" });
  const [payPayout, setPayPayout] = useState(null); // staff member's saved payout details
  const [payVia, setPayVia] = useState("");
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/staff-compensation`, { headers: authHeaders() }).then((r) => setComps(r.data)).catch((e) => {
      if (e.response?.status === 403) setComps([]); // non-owner shell — panel is hidden anyway
      else toast.error("Could not load staff compensation.");
    });
    axios.get(`${API}/admin/staff-compensation/summary`, { headers: authHeaders() }).then((r) => setSummary(r.data)).catch(() => {});
    axios.get(`${API}/admin/team`, { headers: authHeaders() }).then((r) => setTeam(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const assignedIds = useMemo(() => new Set((comps || []).filter((c) => c.active).map((c) => c.staff_user_id)), [comps]);
  const selectable = useMemo(() => team.filter((t) => !assignedIds.has(t.id)), [team, assignedIds]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ staff_user_id: c.staff_user_id, role_title: c.role_title, amount: String(c.amount), frequency: c.frequency, notes: c.notes || "", next_due: c.next_due || "" });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        role_title: form.role_title.trim(),
        amount: parseFloat(form.amount),
        frequency: form.frequency,
        notes: form.notes,
        ...(form.next_due ? { next_due: form.next_due } : {}),
      };
      if (editingId) {
        await axios.patch(`${API}/admin/staff-compensation/${editingId}`, payload, { headers: authHeaders() });
        toast.success("Compensation updated.");
      } else {
        await axios.post(`${API}/admin/staff-compensation`, { ...payload, staff_user_id: form.staff_user_id }, { headers: authHeaders() });
        toast.success("Staff compensation added.");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (e2) { err(e2, "Could not save."); }
    finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/staff-compensation/${c.id}`, { active: !c.active }, { headers: authHeaders() });
      toast.success(c.active ? "Arrangement paused — it stops counting toward projections." : "Arrangement reactivated.");
      load();
    } catch (e) { err(e, "Could not update."); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove the ${c.role_title} arrangement for ${c.staff_name}? Past payment records are kept.`)) return;
    try {
      await axios.delete(`${API}/admin/staff-compensation/${c.id}`, { headers: authHeaders() });
      toast.success("Arrangement removed — payment history preserved.");
      load();
    } catch (e) { err(e, "Delete failed."); }
  };

  const openPay = (c) => {
    setPayTarget(c);
    setPayForm({ amount: String(c.amount), method: "", transaction_id: "", note: "" });
    setPayVia("");
    setPayPayout(null);
    refreshPayoutFor(c.staff_user_id);
  };

  const refreshPayoutFor = (staffUserId) => {
    axios.get(`${API}/admin/workers/${staffUserId}/payment-details`, { headers: authHeaders() })
      .then((r) => setPayPayout(r.data))
      .catch(() => {});
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/staff-compensation/${payTarget.id}/mark-paid`, {
        amount: parseFloat(payForm.amount) || undefined,
        method: payForm.method || undefined,
        transaction_id: payForm.transaction_id || undefined,
        note: payForm.note || undefined,
        payout_via: payVia || undefined,
      }, { headers: authHeaders() });
      toast.success(`Marked paid — next ${payTarget.frequency} payment due ${data.next_due}.`);
      setPayTarget(null);
      load();
    } catch (e) { err(e, "Could not mark paid."); }
    finally { setBusy(false); }
  };

  const openHistory = async (c) => {
    setHistoryFor(c);
    setHistory([]);
    try {
      const { data } = await axios.get(`${API}/admin/staff-compensation/${c.id}/history`, { headers: authHeaders() });
      setHistory(data);
    } catch { toast.error("Could not load history."); }
  };

  return (
    <div data-testid="staff-comp-section" className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-fuchsia-500/15 text-fuchsia-300">
            <Briefcase className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight text-[var(--pv-text)]">Staff & management pay</h2>
            <p className="mt-0.5 text-sm text-[var(--pv-text-4)]">Recurring compensation for staff (managers, coordinators…). Tracked in budgets, separate from animator job costs. Owner-only.</p>
          </div>
        </div>
        <button data-testid="staff-comp-add-btn" onClick={() => { setEditingId(null); setForm(EMPTY); setShowForm((v) => !v); }} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
          <Plus className="h-3.5 w-3.5" /> Add staff compensation
        </button>
      </div>

      {summary && summary.active_count > 0 && (
        <div data-testid="staff-comp-summary" className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[
            { label: "Staff cost / week", value: summary.weekly_total, testid: "staff-comp-weekly" },
            { label: "Staff cost / month", value: summary.monthly_total, testid: "staff-comp-monthly" },
            { label: "Staff paid (30d)", value: summary.paid_30d, testid: "staff-comp-paid-30d" },
            { label: "Payments due now", value: summary.due_now, testid: "staff-comp-due", money: false },
          ].map((s) => (
            <div key={s.testid} data-testid={s.testid} className="rounded-3xl border border-fuchsia-500/20 bg-fuchsia-500/5 p-5">
              <p className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">{s.money === false ? s.value : fmtMoney(s.value)}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--pv-text-4)]">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <form data-testid="staff-comp-form" onSubmit={save} className="mt-5 grid gap-3 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-5 sm:grid-cols-2">
          {!editingId && (
            <select data-testid="staff-comp-member" required value={form.staff_user_id} onChange={(e) => setForm((f) => ({ ...f, staff_user_id: e.target.value }))} className={inputCls}>
              <option value="">Select staff member…</option>
              {selectable.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.email} — {t.display_role || t.worker_role || t.account_role}</option>
              ))}
            </select>
          )}
          <input data-testid="staff-comp-title" required minLength={2} value={form.role_title} onChange={(e) => setForm((f) => ({ ...f, role_title: e.target.value }))} placeholder="Role / title (e.g. Production Manager)" className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="staff-comp-amount" required type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Amount (USD)" className={inputCls} />
            <select data-testid="staff-comp-frequency" value={form.frequency} onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))} className={inputCls}>
              {Object.entries(FREQ_LABEL).map(([v, l]) => <option key={v} value={v}>Paid {l}</option>)}
            </select>
          </div>
          <input data-testid="staff-comp-next-due" type="date" value={form.next_due} onChange={(e) => setForm((f) => ({ ...f, next_due: e.target.value }))} className={inputCls} title="Next payment due (defaults to today)" />
          <input data-testid="staff-comp-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)" className={inputCls} />
          <div className="flex gap-2 sm:col-span-2">
            <button data-testid="staff-comp-save" type="submit" disabled={busy || (!editingId && !form.staff_user_id)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-bold text-white hover:bg-cyan-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} {editingId ? "Save changes" : "Add arrangement"}
            </button>
            <button type="button" data-testid="staff-comp-cancel" onClick={() => { setShowForm(false); setEditingId(null); }} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-5 py-2.5 text-xs font-bold text-[var(--pv-text-3)]">Cancel</button>
          </div>
        </form>
      )}

      {!comps ? (
        <p className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>
      ) : comps.length === 0 ? (
        <p data-testid="staff-comp-empty" className="mt-5 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-12 text-center text-sm text-[var(--pv-text-4)]">
          No staff compensation arrangements yet — add your Production Manager, coordinators or other paid staff here.
        </p>
      ) : (
        <div data-testid="staff-comp-list" className="mt-5 space-y-3">
          {comps.map((c) => (
            <div key={c.id} data-testid={`staff-comp-card-${c.id}`} className={`rounded-3xl border bg-[var(--pv-card)] p-5 ${c.active ? (c.due_now ? "border-amber-500/30" : "border-[var(--pv-border)]") : "border-[var(--pv-border)] opacity-60"}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-base font-bold text-[var(--pv-text)]">{c.staff_name}</p>
                    <span className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-fuchsia-300">{c.role_title}</span>
                    {!c.active && <span className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-2.5 py-0.5 text-[10px] font-bold uppercase text-[var(--pv-text-4)]">inactive</span>}
                    {c.active && c.due_now && (
                      <span data-testid={`staff-comp-due-badge-${c.id}`} className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                        <AlertCircle className="h-3 w-3" /> Payment due
                      </span>
                    )}
                    {c.active && c.due_now && c.payment_status === "pending" && (
                      <span data-testid={`staff-comp-details-required-${c.id}`} className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-amber-300">
                        <Clock className="h-3 w-3" /> Payout pending approval
                      </span>
                    )}
                    {c.active && c.due_now && c.payment_status !== "complete" && c.payment_status !== "pending" && (
                      <span data-testid={`staff-comp-details-required-${c.id}`} className="flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-rose-300">
                        <AlertTriangle className="h-3 w-3" /> Payment details required
                      </span>
                    )}
                    {c.active && !c.due_now && (
                      <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-emerald-300">Paid up</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs font-medium text-[var(--pv-text-4)]">
                    {fmtMoney(c.amount)} {FREQ_LABEL[c.frequency]}
                    <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                    Next due: <span data-testid={`staff-comp-next-due-${c.id}`} className="text-[var(--pv-text-2)]">{c.next_due}</span>
                    <span className="mx-2 text-[var(--pv-text-2)]">|</span>
                    Paid {c.paid_count}× · {fmtMoney(c.paid_total)} total
                  </p>
                  {c.notes && <p className="mt-1 text-xs italic text-[var(--pv-text-4)]">{c.notes}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {c.active && (
                    <button data-testid={`staff-comp-pay-${c.id}`} onClick={() => openPay(c)} className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Mark {c.frequency} paid
                    </button>
                  )}
                  <button data-testid={`staff-comp-history-${c.id}`} onClick={() => openHistory(c)} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400"><History className="h-3 w-3" /> History</button>
                  <button data-testid={`staff-comp-edit-${c.id}`} onClick={() => startEdit(c)} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400"><Pencil className="h-3 w-3" /> Edit</button>
                  <button data-testid={`staff-comp-toggle-${c.id}`} onClick={() => toggleActive(c)} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3 py-1.5 text-[11px] font-bold text-[var(--pv-text-3)] hover:text-cyan-400">{c.active ? "Pause" : "Activate"}</button>
                  <button data-testid={`staff-comp-delete-${c.id}`} onClick={() => remove(c)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-rose-500/10 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {payTarget && (
        <div data-testid="staff-comp-pay-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setPayTarget(null)}>
          <div className="w-full max-w-md rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[var(--pv-text)]">Mark {payTarget.frequency} pay sent</h3>
              <button data-testid="staff-comp-pay-close" onClick={() => setPayTarget(null)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
            </div>
            <p className="mt-1 text-xs text-[var(--pv-text-4)]">{payTarget.staff_name} · {payTarget.role_title} · period due {payTarget.next_due}. Records a paid entry and advances the next due date.</p>
            {payPayout && (
              <div data-testid="staff-pay-payout" className="mt-3 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-3.5">
                {payPayout.status === "missing" || !payPayout.primary ? (
                  <div>
                    <p data-testid="staff-pay-details-required" className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-300">
                      <AlertTriangle className="h-3.5 w-3.5" /> Payment details required — {payTarget.staff_name} hasn't completed a primary payout method yet.
                    </p>
                    <WorkerPayoutSection workerId={payTarget.staff_user_id} compact methods={payPayout.methods} onChanged={() => refreshPayoutFor(payTarget.staff_user_id)} />
                  </div>
                ) : (
                  <div className="space-y-1.5 text-xs">
                    {[["Primary", payPayout.primary], ["Backup", payPayout.backup]].map(([label, s]) => s && (
                      <div key={label} className="flex flex-wrap items-baseline gap-x-2">
                        <span className="w-14 shrink-0 text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">{label}</span>
                        <span className="font-bold text-[var(--pv-text)]">{payPayout.methods?.[s.method]?.label || s.method}</span>
                        <span className="break-all font-medium text-[var(--pv-text-3)]">{slotRows(s, payPayout.methods).filter((r) => r.value).map((r) => r.value).join(" · ")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="mt-4 grid gap-3">
              {payPayout?.primary && (
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Paid via (snapshots their saved details onto this payment)</label>
                  <select data-testid="staff-comp-pay-via" value={payVia} onChange={(e) => setPayVia(e.target.value)} className={inputCls}>
                    <option value="">Manual / other — no snapshot</option>
                    {payPayout.primary && <option value="primary">Primary — {payPayout.methods?.[payPayout.primary.method]?.label || payPayout.primary.method}</option>}
                    {payPayout.backup && <option value="backup">Backup — {payPayout.methods?.[payPayout.backup.method]?.label || payPayout.backup.method}</option>}
                  </select>
                </div>
              )}
              <input data-testid="staff-comp-pay-amount" type="number" min="0.01" step="0.01" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Amount (USD)" className={inputCls} />
              <input data-testid="staff-comp-pay-method" value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} placeholder="Method (e.g. PayPal) — optional" className={inputCls} />
              <input data-testid="staff-comp-pay-txn" value={payForm.transaction_id} onChange={(e) => setPayForm((f) => ({ ...f, transaction_id: e.target.value }))} placeholder="Transaction ID — optional" className={inputCls} />
              <input data-testid="staff-comp-pay-note" value={payForm.note} onChange={(e) => setPayForm((f) => ({ ...f, note: e.target.value }))} placeholder="Note — optional" className={inputCls} />
            </div>
            <button data-testid="staff-comp-pay-confirm" onClick={markPaid} disabled={busy || !(parseFloat(payForm.amount) > 0)} className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-full bg-emerald-500 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm — {fmtMoney(parseFloat(payForm.amount) || 0)} paid
            </button>
          </div>
        </div>
      )}

      {historyFor && (
        <div data-testid="staff-comp-history-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={() => setHistoryFor(null)}>
          <div className="w-full max-w-lg rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[var(--pv-text)]">{historyFor.staff_name} — payment history</h3>
              <button data-testid="staff-comp-history-close" onClick={() => setHistoryFor(null)} className="rounded-full p-2 text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 max-h-80 space-y-2 overflow-y-auto">
              {history.length === 0 ? (
                <p data-testid="staff-comp-history-empty" className="py-8 text-center text-xs text-[var(--pv-text-4)]">No payments recorded yet.</p>
              ) : (
                history.map((p) => (
                  <div key={p.id} data-testid={`staff-comp-history-row-${p.id}`} className="flex items-center justify-between rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-3">
                    <div>
                      <p className="text-xs font-bold text-[var(--pv-text)]">{p.job_title}</p>
                      <p className="text-[10px] text-[var(--pv-text-4)]">
                        {p.paid_at ? new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : ""}
                        {p.method && ` · ${p.method}`}{p.transaction_id && ` · ${p.transaction_id}`}
                      </p>
                      {p.payout_method && (
                        <p data-testid={`staff-comp-history-snapshot-${p.id}`} className="mt-0.5 text-[10px] font-medium text-cyan-300/80">
                          Paid via {p.payout_method.label} ({p.payout_method.slot}) — as saved at payment time
                          {Object.values(p.payout_method.fields || {}).filter(Boolean)[0] ? ` · ${Object.values(p.payout_method.fields || {}).filter(Boolean)[0]}` : ""}
                        </p>
                      )}
                    </div>
                    <span className="font-display text-base font-bold text-emerald-400">{fmtMoney(p.amount)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
